import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useOrgPermissions } from '@/hooks/useOrgPermissions'
import { useToast } from '@/hooks/use-toast'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'
import { formatDistanceToNow } from 'date-fns'
import {
  RefreshCw, Send, Trash2, CheckCheck, Filter, Search, Loader2,
} from 'lucide-react'
import { Twitter, Instagram, Linkedin, Facebook, AtSign } from 'lucide-react'

// ── Platform config ────────────────────────────────────────────────────────────

const PLATFORM_META: Record<string, {
  icon: React.ElementType
  color: string
  label: string
  badge: string
}> = {
  twitter:   { icon: Twitter,   color: 'bg-black',        label: 'X (Twitter)', badge: 'bg-black text-white' },
  linkedin:  { icon: Linkedin,  color: 'bg-[#0A66C2]',    label: 'LinkedIn',    badge: 'bg-[#0A66C2] text-white' },
  facebook:  { icon: Facebook,  color: 'bg-[#1877F2]',    label: 'Facebook',    badge: 'bg-[#1877F2] text-white' },
  instagram: { icon: Instagram, color: 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400', label: 'Instagram', badge: 'bg-pink-500 text-white' },
  threads:   { icon: AtSign,    color: 'bg-black',        label: 'Threads',     badge: 'bg-black text-white' },
}

type DM = {
  id: string
  platform: string
  thread_id: string
  message_id: string
  sender_name: string | null
  sender_handle: string | null
  sender_avatar_url: string | null
  body: string | null
  direction: 'inbound' | 'outbound'
  is_read: boolean
  read_by: string | null
  replied_by: string | null
  replied_at: string | null
  platform_sent_at: string | null
  created_at: string
  profiles_replied?: { full_name: string } | null
  profiles_read?: { full_name: string } | null
}

// ── Helper: group DMs into threads ────────────────────────────────────────────

function groupIntoThreads(dms: DM[]) {
  const threads = new Map<string, { key: string; platform: string; messages: DM[] }>()
  for (const dm of dms) {
    const key = `${dm.platform}:${dm.thread_id}`
    if (!threads.has(key)) threads.set(key, { key, platform: dm.platform, messages: [] })
    threads.get(key)!.messages.push(dm)
  }
  return Array.from(threads.values()).sort((a, b) => {
    const aLast = a.messages[a.messages.length - 1]?.platform_sent_at ?? ''
    const bLast = b.messages[b.messages.length - 1]?.platform_sent_at ?? ''
    return bLast.localeCompare(aLast)
  })
}

// ── Main view ─────────────────────────────────────────────────────────────────

export function SocialDMsView() {
  const { orgId, can } = useOrgPermissions()
  const { toast } = useToast()
  const qc = useQueryClient()

  const [platformFilter, setPlatformFilter] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedThread, setSelectedThread] = useState<string | null>(null)
  const [replyText, setReplyText] = useState('')
  const [syncing, setSyncing] = useState(false)

  const canDelete = can('social_media', 'delete' as any) // CCO/Admin only

  // ── Fetch DMs ──────────────────────────────────────────────────────────────

  const { data: dms = [], isLoading } = useQuery({
    queryKey: ['social-dms', orgId],
    enabled: !!orgId,
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await (supabase
        .from('social_dms' as any)
        .select(`
          *,
          profiles_replied:replied_by(full_name),
          profiles_read:read_by(full_name)
        `)
        .eq('org_id', orgId!)
        .order('platform_sent_at', { ascending: false })
        .limit(500) as any)
      if (error) throw error
      return (data || []) as DM[]
    },
  })

  // ── Realtime subscription ──────────────────────────────────────────────────
  useEffect(() => {
    if (!orgId) return
    const channel = supabase
      .channel('social-dms-realtime')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'social_dms',
        filter: `org_id=eq.${orgId}`,
      }, () => qc.invalidateQueries({ queryKey: ['social-dms', orgId] }))
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [orgId, qc])

  // ── Sync DMs from platforms ────────────────────────────────────────────────
  const handleSync = async () => {
    if (!orgId) return
    setSyncing(true)
    try {
      const { error } = await supabase.functions.invoke('sync-social-dms', { body: { org_id: orgId } })
      if (error) throw error
      await qc.invalidateQueries({ queryKey: ['social-dms', orgId] })
      toast({ title: 'Inbox synced', description: 'Latest messages fetched from all platforms.' })
    } catch (e: any) {
      toast({ title: 'Sync failed', description: e.message, variant: 'destructive' })
    } finally {
      setSyncing(false)
    }
  }

  // ── Mark as read ──────────────────────────────────────────────────────────
  const markReadMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const { data: { user } } = await supabase.auth.getUser()
      const { error } = await (supabase.from('social_dms' as any)
        .update({ is_read: true, read_at: new Date().toISOString(), read_by: user?.id })
        .in('id', ids) as any)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['social-dms', orgId] }),
    onError: (e: any) => toast({ title: 'Failed to mark as read', description: e.message, variant: 'destructive' }),
  })

  // ── Reply ─────────────────────────────────────────────────────────────────
  const replyMutation = useMutation({
    mutationFn: async ({ dm_id, body }: { dm_id: string; body: string }) => {
      const { data, error } = await supabase.functions.invoke('send-social-dm-reply', { body: { dm_id, body } })
      if (error) throw error
      if (data?.error) throw new Error(data.error)
      return data
    },
    onSuccess: () => {
      setReplyText('')
      qc.invalidateQueries({ queryKey: ['social-dms', orgId] })
      toast({ title: 'Reply sent' })
    },
    onError: (e: any) => toast({ title: 'Failed to send reply', description: e.message, variant: 'destructive' }),
  })

  // ── Delete ────────────────────────────────────────────────────────────────
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase.from('social_dms' as any).delete().eq('id', id) as any)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['social-dms', orgId] }),
    onError: (e: any) => toast({ title: 'Delete failed', description: e.message, variant: 'destructive' }),
  })

  // ── Filter + group ────────────────────────────────────────────────────────
  const filtered = dms.filter(dm => {
    if (platformFilter !== 'all' && dm.platform !== platformFilter) return false
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      return (
        dm.sender_name?.toLowerCase().includes(q) ||
        dm.sender_handle?.toLowerCase().includes(q) ||
        dm.body?.toLowerCase().includes(q)
      )
    }
    return true
  })

  const threads = groupIntoThreads(filtered)
  const activeThread = threads.find(t => t.key === selectedThread)
  const activeMessages = activeThread
    ? [...activeThread.messages].sort((a, b) =>
        (a.platform_sent_at ?? '').localeCompare(b.platform_sent_at ?? ''))
    : []
  const lastInboundInThread = activeMessages.filter(m => m.direction === 'inbound').at(-1)

  const unreadCount = dms.filter(d => !d.is_read && d.direction === 'inbound').length

  // Mark thread messages as read when opening
  useEffect(() => {
    if (!selectedThread || !activeMessages.length) return
    const unreadIds = activeMessages.filter(m => !m.is_read && m.direction === 'inbound').map(m => m.id)
    if (unreadIds.length) markReadMutation.mutate(unreadIds)
  }, [selectedThread])

  const bottomRef = useRef<HTMLDivElement>(null)
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [activeMessages.length, selectedThread])

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex h-[calc(100vh-120px)] overflow-hidden rounded-xl border border-border bg-background">

      {/* ── Thread list (left panel) ── */}
      <div className="w-80 shrink-0 flex flex-col border-r border-border">

        {/* Header */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h2 className="text-[15px] font-semibold">Messages</h2>
              {unreadCount > 0 && (
                <span className="text-[10px] font-semibold bg-[#006B3C] text-white rounded-full px-1.5 py-0.5">
                  {unreadCount}
                </span>
              )}
            </div>
            <Button size="sm" variant="ghost" onClick={handleSync} disabled={syncing} className="h-7 w-7 p-0">
              <RefreshCw className={cn('w-3.5 h-3.5', syncing && 'animate-spin')} />
            </Button>
          </div>

          {/* Search */}
          <div className="relative mb-2">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search messages…"
              className="w-full pl-8 pr-3 py-1.5 text-[12.5px] border border-border rounded-lg bg-muted/30 focus:outline-none focus:border-[#006B3C]"
            />
          </div>

          {/* Platform filter */}
          <div className="flex gap-1 flex-wrap">
            {['all', 'twitter', 'linkedin', 'facebook', 'instagram'].map(p => (
              <button
                key={p}
                onClick={() => setPlatformFilter(p)}
                className={cn(
                  'text-[10px] font-medium px-2 py-0.5 rounded-full border transition-colors',
                  platformFilter === p
                    ? 'bg-[#006B3C] text-white border-[#006B3C]'
                    : 'border-border text-muted-foreground hover:border-[#006B3C]/50'
                )}
              >
                {p === 'all' ? 'All' : PLATFORM_META[p]?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Thread list */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : threads.length === 0 ? (
            <div className="text-center py-12 px-4">
              <p className="text-[13px] text-muted-foreground">No messages yet.</p>
              <p className="text-[12px] text-muted-foreground mt-1">Connect accounts and sync to load DMs.</p>
            </div>
          ) : (
            threads.map(thread => {
              const lastMsg = thread.messages.at(-1)!
              const meta = PLATFORM_META[thread.platform]
              const Icon = meta?.icon
              const hasUnread = thread.messages.some(m => !m.is_read && m.direction === 'inbound')
              const isActive = selectedThread === thread.key

              return (
                <button
                  key={thread.key}
                  onClick={() => setSelectedThread(thread.key)}
                  className={cn(
                    'w-full text-left px-4 py-3 border-b border-border/50 hover:bg-muted/40 transition-colors flex gap-3',
                    isActive && 'bg-[#006B3C]/5 border-l-2 border-l-[#006B3C]'
                  )}
                >
                  {/* Avatar with platform badge */}
                  <div className="relative shrink-0">
                    <Avatar className="w-9 h-9">
                      <AvatarImage src={lastMsg.sender_avatar_url ?? undefined} />
                      <AvatarFallback className="text-[11px]">
                        {(lastMsg.sender_name ?? '?')[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className={cn(
                      'absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center',
                      meta?.color
                    )}>
                      {Icon && <Icon className="w-2.5 h-2.5 text-white" />}
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1">
                      <span className={cn('text-[13px] truncate', hasUnread ? 'font-semibold text-foreground' : 'text-foreground/80')}>
                        {lastMsg.sender_name ?? lastMsg.sender_handle ?? 'Unknown'}
                      </span>
                      <span className="text-[10px] text-muted-foreground shrink-0">
                        {lastMsg.platform_sent_at
                          ? formatDistanceToNow(new Date(lastMsg.platform_sent_at), { addSuffix: false })
                          : ''}
                      </span>
                    </div>
                    <p className={cn('text-[12px] truncate mt-0.5', hasUnread ? 'text-foreground font-medium' : 'text-muted-foreground')}>
                      {lastMsg.direction === 'outbound' && <span className="text-[#006B3C]">You: </span>}
                      {lastMsg.body ?? '(attachment)'}
                    </p>
                    {hasUnread && (
                      <span className="inline-block mt-0.5 w-1.5 h-1.5 rounded-full bg-[#006B3C]" />
                    )}
                  </div>
                </button>
              )
            })
          )}
        </div>
      </div>

      {/* ── Conversation panel (right) ── */}
      {!selectedThread ? (
        <div className="flex-1 flex items-center justify-center text-center p-8">
          <div>
            <div className="flex justify-center gap-3 mb-4 opacity-30">
              <Twitter className="w-6 h-6" />
              <Linkedin className="w-6 h-6" />
              <Facebook className="w-6 h-6" />
              <Instagram className="w-6 h-6" />
            </div>
            <p className="text-[14px] font-medium text-foreground/60">Select a conversation</p>
            <p className="text-[12px] text-muted-foreground mt-1">
              All messages from connected accounts appear here
            </p>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-w-0">

          {/* Thread header */}
          {activeThread && (() => {
            const meta = PLATFORM_META[activeThread.platform]
            const Icon = meta?.icon
            const latest = activeMessages.at(-1)
            return (
              <div className="flex items-center gap-3 px-5 py-3.5 border-b border-border">
                <div className="relative">
                  <Avatar className="w-9 h-9">
                    <AvatarImage src={latest?.sender_avatar_url ?? undefined} />
                    <AvatarFallback>{(latest?.sender_name ?? '?')[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className={cn('absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center', meta?.color)}>
                    {Icon && <Icon className="w-2.5 h-2.5 text-white" />}
                  </div>
                </div>
                <div>
                  <p className="text-[14px] font-semibold">
                    {latest?.sender_name ?? latest?.sender_handle ?? 'Unknown'}
                  </p>
                  <span className={cn('text-[10px] font-medium px-1.5 py-0.5 rounded-full', meta?.badge)}>
                    {meta?.label}
                  </span>
                </div>
              </div>
            )
          })()}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
            {activeMessages.map(msg => {
              const isOut = msg.direction === 'outbound'
              const meta = PLATFORM_META[msg.platform]
              const repliedByName = (msg as any).profiles_replied?.full_name

              return (
                <div key={msg.id} className={cn('flex gap-2', isOut && 'flex-row-reverse')}>
                  {!isOut && (
                    <Avatar className="w-7 h-7 shrink-0 mt-1">
                      <AvatarImage src={msg.sender_avatar_url ?? undefined} />
                      <AvatarFallback className="text-[10px]">
                        {(msg.sender_name ?? '?')[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  )}

                  <div className={cn('max-w-[70%] space-y-1', isOut && 'items-end flex flex-col')}>
                    <div className={cn(
                      'px-3.5 py-2.5 rounded-2xl text-[13px] leading-relaxed',
                      isOut
                        ? 'bg-[#006B3C] text-white rounded-tr-sm'
                        : 'bg-muted text-foreground rounded-tl-sm'
                    )}>
                      {msg.body ?? <em className="opacity-50">(attachment)</em>}
                    </div>

                    <div className={cn('flex items-center gap-1.5 text-[10px] text-muted-foreground', isOut && 'flex-row-reverse')}>
                      <span>
                        {msg.platform_sent_at
                          ? formatDistanceToNow(new Date(msg.platform_sent_at), { addSuffix: true })
                          : ''}
                      </span>
                      {isOut && repliedByName && (
                        <>
                          <span>·</span>
                          <CheckCheck className="w-3 h-3 text-[#006B3C]" />
                          <span>{repliedByName}</span>
                        </>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => window.confirm('Delete this message? This cannot be undone.') && deleteMutation.mutate(msg.id)}
                          className="opacity-0 group-hover:opacity-100 hover:text-destructive transition-opacity ml-1"
                          title="Delete message (Institution Admin only)"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>

                  {canDelete && (
                    <button
                      onClick={() => window.confirm('Delete this message? This cannot be undone.') && deleteMutation.mutate(msg.id)}
                      className="self-center opacity-0 hover:opacity-100 focus:opacity-100 p-1 rounded hover:text-destructive transition-opacity"
                      title="Delete (Institution Admin only)"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              )
            })}
            <div ref={bottomRef} />
          </div>

          {/* Reply box */}
          <div className="px-5 py-4 border-t border-border">
            {lastInboundInThread?.replied_by && (
              <p className="text-[11px] text-muted-foreground mb-2 flex items-center gap-1">
                <CheckCheck className="w-3 h-3 text-[#006B3C]" />
                Last replied by <strong>{(lastInboundInThread as any).profiles_replied?.full_name ?? 'a team member'}</strong>
                {lastInboundInThread.replied_at && (
                  <> · {formatDistanceToNow(new Date(lastInboundInThread.replied_at), { addSuffix: true })}</>
                )}
              </p>
            )}
            <div className="flex gap-2 items-end">
              <Textarea
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                placeholder="Write a reply…"
                rows={2}
                className="resize-none text-[13px] flex-1"
                onKeyDown={e => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && replyText.trim() && lastInboundInThread) {
                    e.preventDefault()
                    replyMutation.mutate({ dm_id: lastInboundInThread.id, body: replyText.trim() })
                  }
                }}
              />
              <Button
                onClick={() => {
                  if (replyText.trim() && lastInboundInThread) {
                    replyMutation.mutate({ dm_id: lastInboundInThread.id, body: replyText.trim() })
                  }
                }}
                disabled={!replyText.trim() || replyMutation.isPending || !lastInboundInThread}
                className="bg-[#006B3C] hover:bg-[#005a32] text-white h-[60px] px-4"
              >
                {replyMutation.isPending
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Send className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-[10.5px] text-muted-foreground mt-1.5">⌘ Enter to send</p>
          </div>
        </div>
      )}
    </div>
  )
}
